import { useCallback, useEffect, useRef, useState } from 'react';
import {
  TRUST_AUTOSAVE_DEBOUNCE_MS,
  TRUST_AUTOSAVE_SAVED_FADE_MS,
  type TrustAutosaveStatus,
} from '../lib/trust/trustAutosaveTypes';
import { withRetry } from '../lib/retry';

export type UseTrustAutosaveOptions = {
  enabled: boolean;
  focusIndex: number | null;
  draftScore: string;
  draftFeedback: string;
  isOnline: boolean;
  debounceMs?: number;
  onCommit: (index: number, scoreStr: string, feedback: string) => void | Promise<void>;
  onAfterCommit?: () => void;
};

export type UseTrustAutosaveResult = {
  status: TrustAutosaveStatus;
  retry: () => void;
  flush: () => void;
};

export function useTrustAutosave({
  enabled,
  focusIndex,
  draftScore,
  draftFeedback,
  isOnline,
  debounceMs = TRUST_AUTOSAVE_DEBOUNCE_MS,
  onCommit,
  onAfterCommit,
}: UseTrustAutosaveOptions): UseTrustAutosaveResult {
  const [status, setStatus] = useState<TrustAutosaveStatus>('idle');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const commitRef = useRef(onCommit);
  const afterRef = useRef(onAfterCommit);
  const payloadRef = useRef({ focusIndex, draftScore, draftFeedback, isOnline });

  commitRef.current = onCommit;
  afterRef.current = onAfterCommit;
  payloadRef.current = { focusIndex, draftScore, draftFeedback, isOnline };

  const clearTimers = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (fadeRef.current) {
      clearTimeout(fadeRef.current);
      fadeRef.current = null;
    }
  }, []);

  const scheduleFadeToIdle = useCallback(() => {
    if (fadeRef.current) clearTimeout(fadeRef.current);
    fadeRef.current = setTimeout(() => {
      setStatus((s) => (s === 'saved' || s === 'offline' ? 'idle' : s));
    }, TRUST_AUTOSAVE_SAVED_FADE_MS);
  }, []);

  const runSave = useCallback(async () => {
    const {
      focusIndex: idx,
      draftScore: score,
      draftFeedback: feedback,
      isOnline: online,
    } = payloadRef.current;
    if (!enabled || idx === null) {
      setStatus('idle');
      return;
    }

    clearTimers();

    const commitLocal = async () => {
      await Promise.resolve(commitRef.current(idx, score, feedback));
      afterRef.current?.();
    };

    if (!online) {
      try {
        await commitLocal();
        setStatus('offline');
        scheduleFadeToIdle();
      } catch {
        setStatus('error');
      }
      return;
    }

    setStatus('saving');
    try {
      await withRetry(commitLocal, { maxAttempts: 2, delayMs: 400, backoff: 'fixed' });
      setStatus('saved');
      scheduleFadeToIdle();
    } catch {
      setStatus('error');
    }
  }, [enabled, clearTimers, scheduleFadeToIdle]);

  const flush = useCallback(() => {
    clearTimers();
    void runSave();
  }, [clearTimers, runSave]);

  const retry = useCallback(() => {
    void runSave();
  }, [runSave]);

  useEffect(() => {
    if (!enabled || focusIndex === null) {
      clearTimers();
      setStatus('idle');
      return;
    }

    setStatus((s) => (s === 'error' ? 'error' : 'idle'));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void runSave();
    }, debounceMs);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [enabled, focusIndex, draftScore, draftFeedback, debounceMs, runSave, clearTimers]);

  useEffect(() => {
    return () => clearTimers();
  }, [clearTimers]);

  return { status, retry, flush };
}
